import React, { useMemo } from "react";
import { Box, Grid, HStack, Stack, Text } from "@chakra-ui/react";
import { DashboardDeleteButton, DashboardInput } from "components/utils/FormControls";
import { Button } from "components/ui/Button";
import { MdTimer } from "react-icons/md";
import { FiPlus } from "react-icons/fi";
import { DataCard } from "components/utils/DataCard";
import { FormDialog } from "components/utils/FormDialog";
import { getAvatarHexColor, getAvatarTint } from "utils/avatar";
import { useDialogForm } from "utils/useDialogForm";

function makeRowId() {
  return `hor_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

function parseTimeToMinutes(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;

  const ampm = /^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(AM|PM)$/i.exec(s);
  if (ampm) {
    let h = Number(ampm[1]);
    const min = ampm[2] == null ? 0 : Number(ampm[2]);
    if (!Number.isFinite(h) || !Number.isFinite(min) || h < 1 || h > 12 || min < 0 || min > 59) return null;
    const period = ampm[4].toUpperCase();
    if (period === "AM" && h === 12) h = 0;
    if (period === "PM" && h !== 12) h += 12;
    return h * 60 + min;
  }

  const h24 = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(s);
  if (!h24) return null;

  const h = Number(h24[1]);
  const min = Number(h24[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

function normalizeTimeHHMM(value) {
  const minutes = parseTimeToMinutes(value);
  if (minutes == null) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function addMinutesHHMM(value, deltaMinutes) {
  const baseMinutes = parseTimeToMinutes(value);
  if (baseMinutes == null) return "";
  const day = 24 * 60;
  const total = (((baseMinutes + deltaMinutes) % day) + day) % day;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function formatTimeAMPM(value) {
  const minutes = parseTimeToMinutes(value);
  if (minutes == null) return "";
  const h24 = Math.floor(minutes / 60);
  const min = minutes % 60;
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  return `${h12}:${String(min).padStart(2, "0")} ${period}`;
}

function normalizeHorarioRows(items) {
  if (!Array.isArray(items)) return [];
  return items.map((it) => ({
    id: it?.id || makeRowId(),
    time: normalizeTimeHHMM(it?.time),
    description: String(it?.description ?? ""),
  }));
}

function compareHorarioItems(a, b) {
  const ta = parseTimeToMinutes(a?.time);
  const tb = parseTimeToMinutes(b?.time);
  if (ta != null && tb != null) return ta - tb;
  if (ta != null) return -1;
  if (tb != null) return 1;
  return String(a?.time ?? "").localeCompare(String(b?.time ?? ""));
}

function rowTone(seed) {
  const solid = getAvatarHexColor(seed);
  return { solid, bg: getAvatarTint(seed, 12) };
}

export const PrevioHorarioCard = ({ actividad, actividadId, patch }) => {
  const dialog = useDialogForm({ horario: { items: [] } });

  const saving = patch.isPending;
  const patchError = patch.isError ? String(patch.error?.message || patch.error) : null;

  const horarioItems = useMemo(() => (Array.isArray(actividad?.horario?.items) ? actividad.horario.items : []), [actividad]);
  const horarioSorted = useMemo(() => [...horarioItems].sort(compareHorarioItems), [horarioItems]);

  function getDialogRows() {
    return normalizeHorarioRows(dialog.form?.horario?.items);
  }

  function setDialogRows(rows) {
    dialog.setField("horario", { items: rows });
  }

  function openDialog() {
    setDialogRows(normalizeHorarioRows(actividad?.horario?.items));
    dialog.setOpen(true);
  }

  function addRow() {
    const rows = getDialogRows();
    const previousWithTime = [...rows].reverse().find((r) => !!normalizeTimeHHMM(r?.time));
    const actividadStart = normalizeTimeHHMM(actividad?.horaInicio);
    const defaultTime = previousWithTime ? addMinutesHHMM(previousWithTime.time, 30) : actividadStart;

    setDialogRows([
      ...rows,
      {
        id: makeRowId(),
        time: defaultTime || "",
        description: "",
      },
    ]);
  }

  function removeRow(rowId) {
    setDialogRows(getDialogRows().filter((r) => r.id !== rowId));
  }

  function patchRow(rowId, patchData) {
    setDialogRows(getDialogRows().map((r) => (r.id === rowId ? { ...r, ...patchData } : r)));
  }

  async function save() {
    const items = getDialogRows()
      .map((r) => ({
        time: normalizeTimeHHMM(r.time),
        description: String(r.description ?? "").trim(),
      }))
      .filter((x) => x.time !== "" || x.description !== "");

    await patch.mutateAsync({
      actividadId,
      patch: { horario: { items } },
    });
    dialog.close();
  }

  const editRows = getDialogRows();

  return (
    <>
      <DataCard title="Horario" variant="blue" headerIcon={<MdTimer size="24px" />} onEdit={openDialog} editAriaLabel="Editar horario">
        {horarioSorted.length === 0 ? (
          <Text color="dashboard.fg.muted" fontSize="sm">Sin horario definido.</Text>
        ) : (
          <Grid templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }} gap="2.5" minW="0">
            {horarioSorted.map((it, idx) => {
              const timeLabel = formatTimeAMPM(it?.time) || "Sin hora";
              const description = it?.description?.trim() || "Sin descripcion";
              const tone = rowTone(it?.id || it?.time || description || idx);

              return (
                <Box key={it?.id ?? idx} rounded="2xl" p="3" bg="dashboard.bg.surfaceMuted" borderWidth="1px" borderColor="dashboard.border.muted" minW="0">
                  <Stack gap="2" minW="0">
                    <Box alignSelf="start" rounded="pill" px="3" py="1.5" bg={tone.solid} color="white" fontWeight="900" fontSize="sm" lineHeight="1">
                      {timeLabel}
                    </Box>
                    <Text fontSize="sm" fontWeight="700" color="dashboard.fg.default" whiteSpace="normal" overflowWrap="anywhere" minW="0" flex="1">
                      {description}
                    </Text>
                  </Stack>
                </Box>
              );
            })}
          </Grid>
        )}
      </DataCard>

      <FormDialog open={dialog.open} onOpenChange={dialog.onOpenChange} title="Editar Horario" errorText={patchError} saving={saving} onCancel={dialog.close} onSave={save}>
        <Box>
          <HStack justify="space-between" mb="2">
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default">Horas</Text>
            <Button variant="surface" color="red" size="sm" onClick={addRow}>
              <HStack gap="2">
                <FiPlus />
                <Text>Agregar hora</Text>
              </HStack>
            </Button>
          </HStack>

          {editRows.length === 0 ? (
            <Text color="dashboard.fg.muted">Sin horas. Agrega una para comenzar.</Text>
          ) : (
            <Stack gap="2">
              <Grid templateColumns="130px 1fr auto" gap="3" px="2" py="2" bg="dashboard.bg.surfaceMuted" rounded="md">
                <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Hora</Text>
                <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Descripcion</Text>
                <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted"></Text>
              </Grid>

              {editRows.map((row) => (
                <Grid key={row.id} templateColumns="130px 1fr auto" gap="3" px="2" py="2" alignItems="center">
                  <DashboardInput
                    type="time"
                    step={60}
                    value={row.time || ""}
                    onChange={(e) => patchRow(row.id, { time: e.target.value })}
                  />
                  <DashboardInput
                    value={row.description || ""}
                    onChange={(e) => patchRow(row.id, { description: e.target.value })}
                    placeholder="Ej: Calentamiento"
                  />
                  <DashboardDeleteButton aria-label="Eliminar hora" onClick={() => removeRow(row.id)} />
                </Grid>
              ))}
            </Stack>
          )}
        </Box>
      </FormDialog>
    </>
  );
};
